/**
 * <rendi-finder> — finder YMM in homepage (ENG-145). Ospita più istanze di
 * rendi-choice-group per la stessa dimensione (card compatta mobile, drawer,
 * barra desktop, popover desktop) e le tiene sincronizzate; costruisce l'URL
 * del listing (Search API, ENG-266) via RendiFinderUrl e naviga su "Scopri i
 * prodotti".
 *
 * Tre dimensioni: gender (single-select, radio), eta e categoria (multi-select,
 * checkbox). Lo stato di ciascuna è un array di valori del facet; l'URL è la
 * stessa funzione pura per tutte (chiave ripetuta = OR nel facet).
 *
 * Config letta da uno <script type="application/json" data-finder-config>
 * figlio: { "baseUrl": "...", "filters": { "gender": "gender", "eta": "size",
 * "categoria": "product_type" } }.
 */
if (!customElements.get('rendi-finder')) {
  var FINDER_DIMENSIONS = ['gender', 'eta', 'categoria'];

  // Aria sotto il "Avanti" quando lo si porta a vista nel drawer: scoprirlo a
  // filo del bordo lo farebbe leggere come tagliato.
  var FOOTER_REVEAL_GAP = 12;

  // Match "coerente" tra query libera e label categoria: normalizza (minuscole,
  // niente accenti), poi oltre alla sottostringa piena confronta parola-per-parola
  // per prefisso di ≥4 char, così "maglietta" trova "T-shirt e maglie" e
  // "Maglieria e cardigan" (come HF D6). Le parole di query <3 char si ignorano.
  function catNormalize(s) {
    return String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
  function catMatches(label, query) {
    var q = catNormalize(query).trim();
    if (!q) return true;
    var nl = catNormalize(label);
    if (nl.indexOf(q) !== -1) return true;
    var qWords = q.split(/[^a-z0-9]+/).filter(function (w) { return w.length >= 3; });
    var lWords = nl.split(/[^a-z0-9]+/).filter(function (w) { return w.length >= 2; });
    return qWords.some(function (qw) {
      return lWords.some(function (lw) {
        if (qw === lw) return true;
        return qw.length >= 4 && lw.length >= 4 && qw.slice(0, 4) === lw.slice(0, 4);
      });
    });
  }

  class RendiFinder extends HTMLElement {
    connectedCallback() {
      var configNode = this.querySelector('[data-finder-config]');
      var config = configNode ? JSON.parse(configNode.textContent) : { baseUrl: '', filters: {} };
      this.baseUrl = config.baseUrl || '';
      this.filterIds = config.filters || {};
      this.i18n = config.i18n || {};
      this.state = { gender: [], eta: [], categoria: [] };
      this.catSearch = '';

      // Una dimensione può avere più gruppi (card compatta, drawer, barra e
      // popover desktop): li raccogliamo per tenerli in sync.
      this.groups = {};
      FINDER_DIMENSIONS.forEach(function (dimension) {
        this.groups[dimension] = Array.from(
          this.querySelectorAll('rendi-choice-group[data-name="' + dimension + '"]')
        );
      }, this);

      // Popover desktop: un solo popover aperto per volta.
      this.popovers = Array.from(this.querySelectorAll('[data-finder-popover]'));
      this.openPopoverKey = null;

      this.handleFieldChangeBound = this.handleFieldChange.bind(this);
      this.handleClickBound = this.handleClick.bind(this);
      this.handleInputBound = this.handleInput.bind(this);
      this.handleFocusOutBound = this.handleFocusOut.bind(this);
      this.handleDocClickBound = this.handleDocClick.bind(this);
      this.handleKeydownBound = this.handleKeydown.bind(this);
      this.addEventListener('rendi:field-change', this.handleFieldChangeBound);
      this.addEventListener('click', this.handleClickBound);
      this.addEventListener('input', this.handleInputBound);
      this.addEventListener('focusout', this.handleFocusOutBound);

      this.updateSummaries();
      this.updateCatTokens();
    }

    disconnectedCallback() {
      this.removeEventListener('rendi:field-change', this.handleFieldChangeBound);
      this.removeEventListener('click', this.handleClickBound);
      this.removeEventListener('input', this.handleInputBound);
      this.removeEventListener('focusout', this.handleFocusOutBound);
      document.removeEventListener('click', this.handleDocClickBound);
      document.removeEventListener('keydown', this.handleKeydownBound);
    }

    handleFieldChange(event) {
      var name = event.detail.name;
      if (FINDER_DIMENSIONS.indexOf(name) === -1) return;

      var wasEmpty = this.state[name].length === 0;
      this.state[name] = event.detail.values || [];

      // Rispecchia la selezione sulle altre istanze della stessa dimensione.
      var values = this.state[name];
      this.groups[name].forEach(function (group) {
        if (group === event.target) return;
        group.setValues(values, { silent: true });
      });

      this.updateSummaries();
      if (name === 'categoria') this.updateCatTokens();

      // Scelto il genere sulla barra desktop, il finder apre da solo lo step
      // successivo per insegnare il percorso (review Thomas, 27/07/2026). Non è
      // un gate: Età e Categoria restano cliccabili anche prima del genere
      // ("ingresso soft, non gate", decisione 3 della spec). Sul mobile non
      // serve: il tap sul chip apre già il drawer con l'accordion Età espanso.
      if (
        name === 'gender' &&
        values.length &&
        this.openPopoverKey !== 'eta' &&
        event.target.closest('.rendi-finder__desktop')
      ) {
        this.openPopoverByKey('eta');
      }

      // Prima età scelta nel drawer mobile: la lista chip (mesi + anni) è lunga
      // e l'utente può aver scrollato lontano da "Avanti" per arrivarci. Solo
      // mobile: il popover desktop non ha questo accordion, è già compatto.
      if (
        name === 'eta' &&
        wasEmpty &&
        values.length &&
        event.target.closest('.rendi-finder__mobile')
      ) {
        this.revealAccordionFooter('eta', true);
      }
    }

    // Apre il drawer mobile. Guidato dal CLICK sulla card compatta (non dal
    // change del genere): un re-tap sullo stesso genere già scelto emette
    // 'click' ma non 'change', quindi legare l'apertura al change lasciava un
    // vicolo cieco (chiuso il drawer, riaprirlo era impossibile senza cambiare
    // genere). Il drawer non usa i pannelli di rendi-bottom-sheet (usa
    // l'accordion [data-finder-acc]), quindi open() senza argomenti.
    openDrawer(focusReturn) {
      var sheet = this.querySelector('rendi-bottom-sheet');
      if (!sheet) return;
      if (focusReturn) sheet.lastFocused = focusReturn;
      sheet.open();
      this.revealAccordionFooter('eta');
    }

    // Il drawer si apre con lo step Età già espanso, ma il suo "Avanti" cade
    // sotto il bordo inferiore del body dello sheet: l'utente non vede che c'è
    // un passo successivo (review Thomas, 27/07/2026). Scorriamo il body dello
    // sheet quel tanto che basta a scoprirlo, mai la finestra.
    // smooth: instant di default (usato all'apertura del drawer, dove uno scroll
    // animato correrebbe insieme alla transizione dello sheet in salita — due
    // animazioni in concorrenza). Chi rivela il footer a drawer già fermo (es.
    // dopo aver scelto un'età) passa smooth=true.
    revealAccordionFooter(key, smooth) {
      var acc = this.querySelector('[data-finder-acc="' + key + '"]');
      var footer = acc && acc.querySelector('.rendi-finder__acc-footer');
      var body = this.querySelector('.rendi-sheet__body');
      // La sticky bar (Cancella tutto / Scopri i prodotti) è sempre visibile su
      // mobile e sta *sopra* al fondo del body, coprendone l'ultima fetta: il
      // bordo inferiore del body non è il confine visibile reale, lo è il bordo
      // superiore della sticky bar (altrimenti "Avanti" risulta rivelato sulla
      // carta ma resta nascosto sotto la barra — bug osservato in review).
      var stickyBar = this.querySelector('.rendi-finder__drawer-footer');
      if (!footer || !body) return;

      // Due frame: il primo lo consuma rendi-bottom-sheet per far partire la
      // transizione, il secondo restituisce una geometria stabile. La
      // *differenza* fra i due rect è immune alla translate del surface, quindi
      // è misurabile anche mentre il drawer sta ancora salendo — ed è proprio
      // per questo che lo scroll è istantaneo: il contenuto arriva già nella
      // posizione giusta, senza una seconda animazione in concorrenza.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var visibleBottom = stickyBar ? stickyBar.getBoundingClientRect().top : body.getBoundingClientRect().bottom;
          var overflow = footer.getBoundingClientRect().bottom - visibleBottom;
          if (overflow <= 0) return;
          var top = body.scrollTop + overflow + FOOTER_REVEAL_GAP;
          if (smooth) {
            body.scrollTo({ top: top, behavior: 'smooth' });
          } else {
            body.scrollTop = top;
          }
        });
      });
    }

    handleClick(event) {
      var trigger = event.target.closest('[data-finder-popover-trigger]');
      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        this.togglePopover(trigger.getAttribute('data-finder-popover-trigger'));
        return;
      }

      var next = event.target.closest('[data-finder-popover-next]');
      if (next) {
        event.preventDefault();
        this.openPopoverByKey(next.getAttribute('data-finder-popover-next'));
        return;
      }

      var more = event.target.closest('[data-finder-cat-more]');
      if (more) {
        event.preventDefault();
        this.toggleMore(more);
        return;
      }

      var accToggle = event.target.closest('[data-finder-acc-toggle]');
      if (accToggle) {
        event.preventDefault();
        this.toggleAccordion(accToggle.getAttribute('data-finder-acc-toggle'));
        return;
      }

      var accNext = event.target.closest('[data-finder-acc-next]');
      if (accNext) {
        event.preventDefault();
        this.openAccordion(accNext.getAttribute('data-finder-acc-next'));
        return;
      }

      var catClear = event.target.closest('[data-finder-cat-clear]');
      if (catClear) {
        event.preventDefault();
        this.clearCatSearch(catClear.closest('.rendi-finder__cat'));
        return;
      }

      var token = event.target.closest('[data-cat-token]');
      if (token) {
        event.preventDefault();
        this.removeCategoria(token.getAttribute('data-cat-token'));
        return;
      }

      // Card compatta mobile: un tap su un chip genere apre il drawer, anche se
      // il genere era già selezionato (il re-tap non emette 'change'). Niente
      // preventDefault/return: il radio nativo deve comunque aggiornarsi.
      var compactGender = event.target.closest('.rendi-finder__card [data-choice-label]');
      if (
        compactGender &&
        event.target.closest('.rendi-finder__mobile') &&
        !event.target.closest('.rendi-finder__desktop')
      ) {
        this.openDrawer(compactGender);
        return;
      }

      if (event.target.closest('[data-finder-clear]')) {
        this.clearAll();
        return;
      }

      if (event.target.closest('[data-finder-submit]')) {
        this.submit();
      }
    }

    // ---- Desktop popovers ----
    findPopover(key) {
      return this.popovers.filter(function (popover) {
        return popover.getAttribute('data-finder-popover') === key;
      })[0];
    }

    findTrigger(key) {
      return this.querySelector('[data-finder-popover-trigger="' + key + '"]');
    }

    togglePopover(key) {
      if (this.openPopoverKey === key) {
        this.closePopover(true);
      } else {
        this.openPopoverByKey(key);
      }
    }

    openPopoverByKey(key) {
      this.closePopover();

      var popover = this.findPopover(key);
      var trigger = this.findTrigger(key);
      if (!popover || !trigger) return;

      popover.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.openPopoverKey = key;

      document.addEventListener('click', this.handleDocClickBound);
      document.addEventListener('keydown', this.handleKeydownBound);

      // Dove va il focus all'apertura: sul campo di ricerca se il popover ne ha
      // uno (categoria: si digita subito), altrimenti sul popover stesso, che è
      // un dialog etichettato. MAI sulle checkbox delle età: sono
      // .visually-hidden, e il focus programmatico su di esse non attiva
      // :focus-visible, quindi da tastiera si resterebbe senza alcun indicatore.
      // preventScroll perché quelle checkbox da 1px, portate in vista, facevano
      // saltare la pagina (stesso accorgimento di rendi-sell-form.js e
      // rendi-search-drawer.js).
      var searchInput = popover.querySelector('[data-finder-cat-search]');
      (searchInput || popover).focus({ preventScroll: true });
    }

    closePopover(restoreFocus) {
      if (!this.openPopoverKey) return;

      var popover = this.findPopover(this.openPopoverKey);
      var trigger = this.findTrigger(this.openPopoverKey);
      if (popover) popover.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      document.removeEventListener('click', this.handleDocClickBound);
      document.removeEventListener('keydown', this.handleKeydownBound);
      this.openPopoverKey = null;

      // Azzera la ricerca categoria transitoria alla chiusura: il segmento torna
      // al placeholder (o al conteggio se ci sono selezioni), non resta l'eco.
      if (this.catSearch) {
        this.catSearch = '';
        Array.from(this.querySelectorAll('.rendi-finder__cat')).forEach(function (cat) {
          var input = cat.querySelector('[data-finder-cat-search]');
          if (input) input.value = '';
          this.filterCategories(cat, '');
        }, this);
        this.updateSummary('categoria');
      }

      if (restoreFocus && trigger) trigger.focus();
    }

    handleDocClick(event) {
      // Ogni interazione col finder (trigger, Avanti, contenuto del popover) sta
      // dentro <rendi-finder> e la gestisce handleClick: qui chiudiamo solo sui
      // click davvero esterni. Questo evita che il listener aggiunto durante lo
      // stesso click (es. su "Avanti") richiuda subito il popover appena aperto.
      // Usiamo composedPath() (catturato al dispatch) invece di contains(target):
      // rimuovere un token dal DOM stacca il bersaglio del click, e contains()
      // darebbe falso → chiusura indesiderata. Il path resta valido comunque.
      if (event.composedPath().includes(this)) return;
      this.closePopover();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePopover(true);
      }
    }

    // Il popover è non-modale: invece di intrappolare il focus, si chiude quando
    // il focus lo lascia (Tab oltre l'ultimo elemento, o su un altro segmento).
    // Evita che il focus finisca dietro un popover ancora aperto (WCAG 2.4.3).
    handleFocusOut(event) {
      if (!this.openPopoverKey) return;
      var next = event.relatedTarget;
      if (!next) return; // focus uscito dalla finestra: non forziamo la chiusura
      var popover = this.findPopover(this.openPopoverKey);
      if (popover && popover.contains(next)) return;
      if (next === this.findTrigger(this.openPopoverKey)) return;
      this.closePopover();
    }

    toggleMore(button) {
      var cat = button.closest('.rendi-finder__cat');
      if (!cat) return;
      var extra = cat.querySelector('[data-cat-tier="extra"]');
      var label = button.querySelector('[data-finder-more-label]');
      var expanded = button.getAttribute('aria-expanded') === 'true';

      if (extra) extra.hidden = expanded;
      button.setAttribute('aria-expanded', String(!expanded));
      if (label) {
        var text = expanded ? button.getAttribute('data-label-more') : button.getAttribute('data-label-less');
        if (text) label.textContent = text;
      }
    }

    // ---- Mobile drawer accordion (una riga aperta per volta) ----
    toggleAccordion(key) {
      var acc = this.querySelector('[data-finder-acc="' + key + '"]');
      if (!acc) return;
      var head = acc.querySelector('[data-finder-acc-toggle]');
      var isOpen = head && head.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.setAccordion(key, false);
      } else {
        this.openAccordion(key);
      }
    }

    openAccordion(key) {
      Array.from(this.querySelectorAll('[data-finder-acc]')).forEach(function (acc) {
        var accKey = acc.getAttribute('data-finder-acc');
        this.setAccordion(accKey, accKey === key);
      }, this);
    }

    setAccordion(key, open) {
      var acc = this.querySelector('[data-finder-acc="' + key + '"]');
      if (!acc) return;
      var head = acc.querySelector('[data-finder-acc-toggle]');
      // The panel open/close is animated in CSS off the head's aria-expanded
      // (grid-template-rows 0fr↔1fr), so we only flip the ARIA state here.
      if (head) head.setAttribute('aria-expanded', String(open));
    }

    // ---- Categoria: token nel campo + filtro per sottostringa ----
    removeCategoria(value) {
      var next = this.state.categoria.filter(function (v) { return v !== value; });
      this.state.categoria = next;
      this.groups.categoria.forEach(function (group) {
        group.setValues(next, { silent: true });
      });
      this.updateSummaries();
      this.updateCatTokens();
    }

    updateCatTokens() {
      var values = this.state.categoria;
      Array.from(this.querySelectorAll('[data-finder-cat-tokens]')).forEach(function (container) {
        container.textContent = '';
        values.forEach(function (value) {
          var token = document.createElement('button');
          token.type = 'button';
          token.className = 'rendi-finder__token';
          token.setAttribute('data-cat-token', value);
          var text = document.createElement('span');
          text.textContent = value;
          var x = document.createElement('span');
          x.className = 'rendi-finder__token-x';
          x.setAttribute('aria-hidden', 'true');
          x.textContent = '×';
          token.appendChild(text);
          token.appendChild(x);
          container.appendChild(token);
        });
        container.classList.toggle('is-empty', values.length === 0);
      });
    }

    handleInput(event) {
      var search = event.target.closest('[data-finder-cat-search]');
      if (!search) return;
      var cat = search.closest('.rendi-finder__cat');
      this.catSearch = search.value.trim();
      if (cat) this.filterCategories(cat, search.value);
      this.updateSummary('categoria');
    }

    clearCatSearch(cat) {
      if (!cat) return;
      var input = cat.querySelector('[data-finder-cat-search]');
      if (input) {
        input.value = '';
        input.focus();
      }
      this.catSearch = '';
      this.filterCategories(cat, '');
      this.updateSummary('categoria');
    }

    filterCategories(cat, query) {
      var q = query.trim();
      var extra = cat.querySelector('[data-cat-tier="extra"]');
      var moreBtn = cat.querySelector('[data-finder-cat-more]');
      var legend = cat.querySelector('[data-finder-cat-legend]');
      var clearBtn = cat.querySelector('[data-finder-cat-clear]');
      var emptyMsg = cat.querySelector('[data-finder-cat-empty]');

      // Il drawer mobile sta in una surface ancorata al bordo inferiore, alta
      // quanto il contenuto: se la lista chip cambia altezza mentre si digita, il
      // bordo *superiore* della surface si sposta e il campo scivola sotto le
      // dita, con le suggestion dietro la tastiera (review Thomas, 27/07/2026).
      // Cambia in entrambi i versi: la prima lettera scopre anche i chip di
      // "Vedi di più" e allunga, poi la query stringe e accorcia.
      // Alla prima battuta congeliamo l'altezza della lista a quella che l'utente
      // aveva davanti — misurata dal bordo alto del fieldset al fondo del box,
      // così include "Vedi di più", che durante la ricerca sparisce — e la CSS
      // la applica con overflow-y:auto, così i match in eccesso restano
      // raggiungibili. Si scongela quando il campo torna vuoto. Misurato qui,
      // prima di toccare i chip. Sul desktop il popover è fuori flusso e cresce
      // verso il basso senza spostare niente: closest() dà null e si salta.
      var listbox = cat.querySelector('.rendi-finder__fieldset');
      if (listbox && cat.closest('.rendi-finder__mobile')) {
        if (!q) {
          cat.classList.remove('is-searching');
          cat.style.removeProperty('--cat-list-height');
        } else if (!cat.classList.contains('is-searching')) {
          // Arrotondata per eccesso: l'altezza reale è frazionaria e mezzo pixel
          // in meno basta a far rientrare lo scostamento che stiamo evitando.
          var frozen = cat.getBoundingClientRect().bottom - listbox.getBoundingClientRect().top;
          cat.style.setProperty('--cat-list-height', Math.ceil(frozen) + 'px');
          cat.classList.add('is-searching');
        }
      }

      // Cercando mostriamo tutti i 16 chip (match anche sotto "Vedi di più"); a
      // campo vuoto si ripristina lo stato collassato secondo il bottone.
      if (q) {
        if (extra) extra.hidden = false;
        if (moreBtn) moreBtn.hidden = true;
      } else {
        var expanded = moreBtn && moreBtn.getAttribute('aria-expanded') === 'true';
        if (extra) extra.hidden = !expanded;
        if (moreBtn) moreBtn.hidden = false;
      }

      var anyVisible = false;
      Array.from(cat.querySelectorAll('.rendi-finder__chip--cat')).forEach(function (chip) {
        var show = catMatches(chip.getAttribute('data-cat-label') || '', q);
        chip.hidden = !show;
        if (show) anyVisible = true;
      });

      // Label dinamica: "Categorie coerenti:" mentre si cerca, "Tutte le
      // categorie" a campo vuoto (HF D4 → D6).
      if (legend) {
        var text = legend.getAttribute(q ? 'data-label-coherent' : 'data-label-all');
        if (text) legend.textContent = text;
      }
      if (clearBtn) clearBtn.hidden = !q;
      if (emptyMsg) emptyMsg.hidden = !(q && !anyVisible);
    }

    clearAll() {
      FINDER_DIMENSIONS.forEach(function (dimension) {
        this.state[dimension] = [];
        this.groups[dimension].forEach(function (group) {
          group.clear({ silent: true });
        });
      }, this);

      this.catSearch = '';
      this.updateSummaries();
      this.updateCatTokens();

      // Azzera anche il filtro di ricerca categoria e ripristina i chip.
      Array.from(this.querySelectorAll('[data-finder-cat-search]')).forEach(function (search) {
        search.value = '';
        var cat = search.closest('.rendi-finder__cat');
        if (cat) this.filterCategories(cat, '');
      }, this);
    }

    // Label leggibili dei valori selezionati di una dimensione, lette dal DOM
    // (i chip). Lo stato tiene i valori Globo grezzi; i riepiloghi mostrano le
    // label dei chip, così non serve una seconda mappa valore→label in JS.
    selectedLabels(dimension) {
      var group = this.groups[dimension] && this.groups[dimension][0];
      if (!group) return [];
      return Array.from(group.querySelectorAll('input:checked')).map(function (input) {
        var label = input.closest('[data-choice-label]');
        var span = label && label.querySelector('.rendi-finder__chip-label');
        return span ? span.textContent.trim() : input.value;
      });
    }

    updateSummaries() {
      this.updateSummary('eta');
      this.updateSummary('categoria');
    }

    updateSummary(dimension) {
      var nodes = this.querySelectorAll('[data-finder-summary="' + dimension + '"]');
      if (!nodes.length) return;

      var count = this.state[dimension].length;
      // Mentre si digita nel campo categoria (e niente è ancora selezionato) il
      // segmento fa da eco alla query digitata, come HF D6.
      var echo = dimension === 'categoria' && count === 0 && this.catSearch ? this.catSearch : '';

      nodes.forEach(function (node) {
        // Ogni punto UI ha il suo testo di stato vuoto (il segmento desktop dice
        // "Seleziona un'età/taglia", l'header del drawer dice "Qualsiasi"): lo
        // legge da data-empty-text sul nodo.
        var text;
        if (echo) {
          text = echo;
        } else if (count === 0) {
          text = node.getAttribute('data-empty-text') || '';
        } else {
          text = this.filledSummary(dimension, count);
        }
        node.textContent = text;
        node.classList.toggle('is-empty', count === 0 && !echo);
      }, this);
    }

    filledSummary(dimension, count) {
      // Età: elenco delle fasce ("3 anni, 5 anni"). Categoria: conteggio
      // ("2 categorie") come nel Figma D7/M7, singolare col nome della sola voce.
      if (dimension === 'eta') {
        return this.selectedLabels('eta').join(', ');
      }

      var labels = this.selectedLabels('categoria');
      if (count === 1) return labels[0];
      var template = (this.i18n && this.i18n.categoriaCount) || '__CATCOUNT__';
      return template.replace('__CATCOUNT__', String(count));
    }

    submit() {
      window.location.href = window.RendiFinderUrl.build(this.baseUrl, this.filterIds, this.state);
    }
  }

  customElements.define('rendi-finder', RendiFinder);
}
